import { Navigate, useParams } from "react-router-dom";

import { useUsers } from "~/hooks";
import { ROUTES } from "~/router";
import {
  CANCEL_REASON_TYPES,
  CancellationReasonFormData,
  useAppointmentsListQuery,
  useCancelVisitMutation,
  useListCancelReasonsQuery,
} from "~/services";
import { type ModalProps } from "~/shared.types";
import { errorToast, Modal, toast } from "~/ui";
import { getFieldErrorMessages } from "~/utils";
import { VisitCard } from "..";
import { CancellationReasonFields } from "../CancellationReason/CancellationReasonFields";
import { isWithinLateCancellationWindow } from "../CancellationReason/is-within-late-cancellation-window";
import { useCancellationReasonForm } from "../CancellationReason/use-cancellation-reason-form";

export const CancelAVisitModal = ({ show, onClose }: ModalProps) => {
  const params = useParams<{ userId: string; appointmentId: string }>();
  const { userId, appointmentId } = params;
  const { getUser } = useUsers();
  const { data } = getUser(userId!);
  const user = data?.data;
  const patientId = user?.ehrId;

  const { data: cancelReasonsList } = useListCancelReasonsQuery(
    CANCEL_REASON_TYPES.INTERNAL,
  );
  const { mutate: cancelVisit, isPending: isCancelingVisit } =
    useCancelVisitMutation();
  const { data: appointments, refetch: refetchAppointments } =
    useAppointmentsListQuery(userId);

  const { upcomingAppointments = [] } = appointments ?? {};

  const visit = upcomingAppointments.find(
    (visit) => visit.id === appointmentId!,
  );

  // Cancelling happens now, so the window is measured from now.
  const isFeeWindowOpen = Boolean(
    visit && isWithinLateCancellationWindow(visit.date),
  );

  const {
    form,
    values,
    selectedParent,
    subReasons,
    showFeeWaiver,
    handleFieldChange,
    handleParentChange,
    validateSubReason,
    setWaiveFee,
  } = useCancellationReasonForm({
    reasons: cancelReasonsList,
    defaultValues: {},
    isFeeWindowOpen,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (formData: CancellationReasonFormData) => {
    // Re-evaluated here rather than reusing the render-time value: the visit can start while
    // the modal is open, and a waiver the server no longer considers applicable fails the
    // whole cancellation with a 422 rather than just the waiver.
    const feeApplies = Boolean(
      selectedParent?.bearsLateFee &&
      visit &&
      isWithinLateCancellationWindow(visit.date),
    );

    cancelVisit(
      {
        cancelVisitData: {
          ...formData,
          appointmentId: appointmentId!,
          patientId: patientId!,
          waiveFee: feeApplies ? formData.waiveFee : undefined,
        },
      },
      {
        onSuccess: () => {
          toast({
            type: "success",
            title: "Visit has been canceled",
          });
          refetchAppointments();
          onClose();
        },
        onError: (error) => {
          errorToast(getFieldErrorMessages(error));
        },
      },
    );
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  if (!appointmentId || !patientId || !visit)
    return <Navigate to={ROUTES.dashboard} />;

  return (
    <Modal
      show={show}
      title="Cancel a visit"
      onClose={onClose}
      handleConfirm={handleConfirm}
      closeLabel="Back"
      confirmLabel="Cancel visit"
      isLoading={isCancelingVisit}
    >
      <form className="flex h-full flex-col gap-6">
        <VisitCard key={appointmentId} visit={visit} />

        <CancellationReasonFields
          reasons={cancelReasonsList}
          subReasons={subReasons}
          parentId={values.cancelReasonParentId || ""}
          childId={values.cancelReasonChildId || ""}
          notes={values.cancellationNotes || ""}
          waiveFee={values.waiveFee ?? false}
          showFeeWaiver={showFeeWaiver}
          errors={{
            parentId: errors.cancelReasonParentId?.message,
            childId: errors.cancelReasonChildId?.message,
            notes: errors.cancellationNotes?.message,
          }}
          onParentChange={handleParentChange}
          onChildChange={(value) =>
            handleFieldChange("cancelReasonChildId", value)
          }
          onChildBlur={validateSubReason}
          onNotesChange={(value) =>
            handleFieldChange("cancellationNotes", value)
          }
          onWaiveFeeChange={setWaiveFee}
        />

        <p className="text-dark-blue-gray mt-auto pt-6 text-sm font-medium">
          The patient will be notified and the time slot will be released.
        </p>
      </form>
    </Modal>
  );
};
